/**
 * Logica pura degli avvisi per i nuovi messaggi in arrivo (WhatsApp,
 * Instagram, Messenger): cosa e' nuovo rispetto all'ultima fotografia, quando
 * avvisare, e la preferenza on/off salvata per utente.
 */

import { isMuted, type ChatListPrefs } from '@/lib/services/whatsapp/chatListRules';

export type NotifyChannel = 'whatsapp' | 'instagram' | 'messenger';

export interface InboundStamp {
  /** Chiave unica della conversazione, prefissata dal canale. */
  key: string;
  channel: NotifyChannel;
  /** Istante dell'ultimo messaggio in arrivo, in millisecondi. */
  at: number;
  name: string;
  preview: string;
}

export interface WhatsAppChatLike {
  id: string;
  name?: string;
  isGroup?: boolean;
  lastMessage?: { body?: string; timestamp?: number; fromMe?: boolean } | null;
}

export interface MetaConversationLike {
  id: string;
  channel: 'instagram' | 'messenger';
  contact_name?: string | null;
  last_inbound_at?: string | null;
  last_message_preview?: string | null;
  last_message_direction?: 'inbound' | 'outbound' | null;
}

/** I timestamp WhatsApp arrivano in secondi, quelli nuovi a volte in ms. */
export function toMillis(ts: number | undefined | null): number {
  if (!ts || !Number.isFinite(ts)) return 0;
  return ts < 1e12 ? ts * 1000 : ts;
}

export function whatsappStamps(chats: WhatsAppChatLike[]): InboundStamp[] {
  const out: InboundStamp[] = [];
  for (const c of chats) {
    const lm = c.lastMessage;
    if (!lm || lm.fromMe) continue;
    const at = toMillis(lm.timestamp);
    if (!at) continue;
    out.push({ key: `wa:${c.id}`, channel: 'whatsapp', at, name: c.name || 'Contatto WhatsApp', preview: lm.body || '' });
  }
  return out;
}

export function metaStamps(conversations: MetaConversationLike[]): InboundStamp[] {
  const out: InboundStamp[] = [];
  for (const c of conversations) {
    const at = c.last_inbound_at ? Date.parse(c.last_inbound_at) : 0;
    if (!at || Number.isNaN(at)) continue;
    const preview = c.last_message_direction === 'inbound' ? c.last_message_preview || '' : '';
    out.push({
      key: `${c.channel}:${c.id}`,
      channel: c.channel,
      at,
      name: c.contact_name || (c.channel === 'instagram' ? 'Contatto Instagram' : 'Contatto Messenger'),
      preview,
    });
  }
  return out;
}

/**
 * Messaggi nuovi rispetto alla fotografia precedente. Una conversazione mai
 * vista conta solo se il messaggio e' successivo a `since` (l'avvio del
 * controllo): cosi' il primo caricamento della lista non fa suonare niente.
 */
export function diffNewInbound(prev: Map<string, number>, next: InboundStamp[], since: number): InboundStamp[] {
  const fresh: InboundStamp[] = [];
  for (const s of next) {
    const before = prev.get(s.key);
    if (before === undefined ? s.at > since : s.at > before) fresh.push(s);
  }
  return fresh;
}

export function stampsToMap(stamps: InboundStamp[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of stamps) m.set(s.key, Math.max(m.get(s.key) ?? 0, s.at));
  return m;
}

/**
 * Chiave delle preferenze della chat (useWhatsAppChatMetaStore.chatMeta):
 * per WhatsApp e' il chat_jid, per Instagram/Messenger 'meta:<id conversazione>'.
 */
export function prefsKeyOfStamp(s: InboundStamp): string {
  const id = s.key.slice(s.key.indexOf(':') + 1);
  return s.channel === 'whatsapp' ? id : `meta:${id}`;
}

/** Toglie le chat silenziate: niente suono ne' notifica (come il numero sul menu). */
export function dropMuted(
  fresh: InboundStamp[],
  prefsOf: (key: string) => ChatListPrefs | null | undefined,
  now: number = Date.now(),
): InboundStamp[] {
  return fresh.filter((s) => !isMuted(prefsOf(prefsKeyOfStamp(s)), now));
}

/** Si avvisa solo se l'utente lo vuole e non sta gia' guardando i messaggi. */
export function shouldNotify(args: { enabled: boolean; drawerOpen: boolean; documentHidden: boolean }): boolean {
  if (!args.enabled) return false;
  return !args.drawerOpen || args.documentHidden;
}

/** Evita la raffica: al massimo un suono ogni `gapMs`. */
export function canPlaySound(lastPlayedAt: number, now: number, gapMs = 4000): boolean {
  return now - lastPlayedAt >= gapMs;
}

export function notificationText(fresh: InboundStamp[]): { title: string; body: string } {
  const labels: Record<NotifyChannel, string> = { whatsapp: 'WhatsApp', instagram: 'Instagram', messenger: 'Messenger' };
  if (fresh.length === 1) {
    const s = fresh[0];
    const preview = s.preview.trim();
    return {
      title: `${s.name} (${labels[s.channel]})`,
      body: preview ? (preview.length > 120 ? `${preview.slice(0, 117)}...` : preview) : 'Nuovo messaggio',
    };
  }
  const names = Array.from(new Set(fresh.map((s) => s.name))).slice(0, 3).join(', ');
  return { title: `${fresh.length} nuovi messaggi`, body: names };
}

// ---- Preferenza per utente ----

export const NOTIFY_PREF_PREFIX = 'flusia_msg_notify_';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export function notifyPrefKey(userId: string | null | undefined): string {
  return `${NOTIFY_PREF_PREFIX}${userId || 'anon'}`;
}

/** Di default gli avvisi sono accesi (il suono e' discreto, il browser chiede il permesso a parte). */
export function readNotifyEnabled(storage: StorageLike | null | undefined, userId: string | null | undefined): boolean {
  try {
    const v = storage?.getItem(notifyPrefKey(userId));
    return v === null || v === undefined ? true : v === 'on';
  } catch {
    return true;
  }
}

export function writeNotifyEnabled(storage: StorageLike | null | undefined, userId: string | null | undefined, enabled: boolean): void {
  try {
    storage?.setItem(notifyPrefKey(userId), enabled ? 'on' : 'off');
  } catch {
    /* ignorato: vale per la visita corrente */
  }
}
